import { formatTime } from '@/lib/date-time';
/**
 * @file routes/_app/auditor/schedule.tsx
 * @description 稽核員行程日曆頁（M2）。
 *
 * 功能：
 * - antd Calendar 顯示稽核任務（按 plannedAuditMonth）
 * - Auditor：只顯示自己的任務
 * - Manager/Admin：顯示所有任務，可按人員過濾
 * - 點擊日期 → 列出當日所在月任務
 *
 * 角色：
 * - admin/manager：看全部
 * - auditor：看自己
 *
 * 視覺：PageShell + SectionCard + StatusPill + antd Calendar
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { Badge, Calendar, Card, Col, List, Row, Select, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { CalendarDays, ClipboardCheck } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { PageShell, SectionCard, StatusPill, type StatusPillProps } from '@/components/shell';

// ─── 組長待審申請文件卡片 ─────────────────────────────────────────────────────

/** dashboard 待審申請文件清單項（對應 service.listPendingReviewsForAuditor 回應） */
interface PendingDocReview {
  reviewId: number;
  caseId: number;
  caseCode: string;
  clientName: string | null;
  submittedAt: string;
  submitterName: string | null;
}

/**
 * 組長首頁待辦：當前 actor 作為某案件「稽核組長」需要決議的申請文件審核。
 * 30 秒輪詢；無待辦時整張卡片隱藏。點擊「去審核」跳該案件詳情頁文件分頁。
 */
function PendingDocReviewsCard() {
  const { data } = useQuery<{ items: PendingDocReview[] }>({
    queryKey: ['auditor-pending-doc-reviews'],
    queryFn: () => apiClient.get('/api/dashboard/auditor/pending-document-reviews'),
    refetchInterval: 30_000,
  });
  const items = data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <Card
      size="small"
      title={
        <Space>
          <ClipboardCheck size={16} />
          <span>待審申請文件（{items.length}）</span>
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <List
        size="small"
        dataSource={items}
        renderItem={(it) => (
          <List.Item
            actions={[
              <Link key="go" to="/dispatch/$caseId" params={{ caseId: String(it.caseId) }}>
                去審核
              </Link>,
            ]}
          >
            <List.Item.Meta
              title={`${it.caseCode}  ${it.clientName ?? ''}`}
              description={`${it.submitterName ?? '管理組'} 於 ${dayjs(it.submittedAt).format('YYYY-MM-DD HH:mm')} 提交`}
            />
          </List.Item>
        )}
      />
    </Card>
  );
}

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/auditor/schedule')({ component: AuditorSchedulePage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface AuditTask {
  id: number;
  caseId: number;
  caseCode: string | null;
  assigneeId: number;
  status: string;
  plannedAuditMonth: string | null; // 'YYYY-MM'
  arrivedAt: string | null;
  leftAt: string | null;
  isTeamLead: boolean;
  createdAt: string;
}

// ─── 任務狀態顏色 ─────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, 'success' | 'processing' | 'default' | 'error' | 'warning'> = {
  已派案: 'default',
  已確認: 'processing',
  現場中: 'warning',
  已完成: 'success',
  已取消: 'error',
};

const STATUS_TONE: Record<string, StatusPillProps['tone']> = {
  已派案: 'muted',
  已確認: 'brand',
  現場中: 'warning',
  已完成: 'success',
  已取消: 'danger',
};

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 稽核員行程日曆頁面。
 */
function AuditorSchedulePage() {
  const user = useAuthStore((s) => s.user!);
  const isManager = user.roles.includes('admin') || user.roles.includes('manager');

  /** 當前選中月份（用於查詢） */
  const [selectedMonth, setSelectedMonth] = useState<string>(dayjs().format('YYYY-MM'));
  /** 當前選中日期（用於顯示當日任務） */
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  /** 人員過濾（manager 用） */
  const [assigneeFilter, setAssigneeFilter] = useState<number | null>(null);

  /** 查詢稽核任務列表 */
  const { data, isLoading } = useQuery<{ items: AuditTask[] }>({
    queryKey: ['audit-tasks-schedule', selectedMonth, assigneeFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (assigneeFilter) params.set('assigneeId', String(assigneeFilter));
      return apiClient.get(`/api/audit-tasks?${params.toString()}`);
    },
  });

  const tasks = data?.items ?? [];

  /**
   * 取得指定月份的任務（plannedAuditMonth 格式 YYYY-MM）
   */
  const getTasksForMonth = (month: string) => tasks.filter((t) => t.plannedAuditMonth === month);

  /**
   * Calendar 每格 dateCellRender：顯示當月有任務的日期 Badge
   * 注意：plannedAuditMonth 精度到月，因此每月第一天顯示本月任務 Badge
   */
  const cellRender = (date: Dayjs) => {
    const month = date.format('YYYY-MM');
    const day = date.date();
    // 只在每月 1 日顯示該月總覽（避免每天都重複顯示）
    if (day !== 1) return null;
    const monthTasks = getTasksForMonth(month);
    if (monthTasks.length === 0) return null;
    return (
      <div>
        {monthTasks.slice(0, 3).map((t) => (
          <Badge
            key={t.id}
            status={STATUS_BADGE[t.status] ?? 'default'}
            text={<span style={{ fontSize: 10 }}>{t.caseCode ?? `任務 #${t.id}`}</span>}
            style={{ display: 'block' }}
          />
        ))}
        {monthTasks.length > 3 && (
          <span style={{ fontSize: 10, color: '#999' }}>+ {monthTasks.length - 3} 筆</span>
        )}
      </div>
    );
  };

  /**
   * 當日選取的任務（顯示在右側列表）
   * 因為 plannedAuditMonth 精度到月，這裡顯示的是 selectedDate 所在月份的任務
   */
  const selectedMonthFromDate = selectedDate.slice(0, 7);
  const tasksForSelectedMonth = getTasksForMonth(selectedMonthFromDate);

  return (
    <PageShell
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CalendarDays size={20} />
          稽核行程日曆
        </span>
      }
      description="按月查看稽核任務排程，點擊日期查看當月所有任務"
      actions={
        isManager ? (
          <Select
            placeholder="過濾人員（全部）"
            allowClear
            style={{ width: 200 }}
            onChange={(v: number | null | undefined) => setAssigneeFilter(v ?? null)}
          />
        ) : undefined
      }
    >
      {/* 組長待審申請文件（無待辦則卡片自動隱藏） */}
      <PendingDocReviewsCard />

      {/**
       * 響應式佈局：
       * - <md（手機）：日曆與列表單列堆疊
       * - md（iPad 直立 768~991）：仍單列堆疊（日曆寬，列表保留滑動空間）
       * - ≥lg：日曆 16 欄 + 列表 8 欄並排
       *
       * gutter [16, 16]：第二個值是 row gutter，堆疊時提供垂直間距。
       */}
      <Row gutter={[16, 16]}>
        {/* 日曆 */}
        <Col xs={24} md={24} lg={16}>
          <SectionCard padless>
            <Calendar
              fullscreen
              cellRender={cellRender}
              onSelect={(date) => {
                const dateStr = date.format('YYYY-MM-DD');
                setSelectedDate(dateStr);
                setSelectedMonth(date.format('YYYY-MM'));
              }}
              onPanelChange={(date) => setSelectedMonth(date.format('YYYY-MM'))}
            />
          </SectionCard>
        </Col>

        {/* 任務列表 */}
        <Col xs={24} md={24} lg={8}>
          <Card title={`${selectedMonthFromDate} 的稽核任務`} size="small" loading={isLoading}>
            {tasksForSelectedMonth.length === 0 ? (
              <div className="py-4 text-center text-gray-400">本月無稽核任務</div>
            ) : (
              <List
                dataSource={tasksForSelectedMonth}
                renderItem={(task) => (
                  <List.Item key={task.id} style={{ padding: '8px 0' }}>
                    <div style={{ width: '100%' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium" style={{ fontFamily: 'monospace' }}>
                          {task.caseCode ?? `任務 #${task.id}`}
                        </span>
                        <StatusPill tone={STATUS_TONE[task.status] ?? 'neutral'} size="sm">
                          {task.status}
                        </StatusPill>
                      </div>
                      {task.isTeamLead && (
                        <div style={{ marginTop: 4 }}>
                          <StatusPill tone="brand" size="sm">
                            隊長
                          </StatusPill>
                        </div>
                      )}
                      {task.arrivedAt && (
                        <div className="mt-1 text-xs text-gray-400">
                          到達：{formatTime(task.arrivedAt)}
                        </div>
                      )}
                      {task.leftAt && (
                        <div className="text-xs text-gray-400">
                          離開：{formatTime(task.leftAt)}
                        </div>
                      )}
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </PageShell>
  );
}
